import { ReactNode, useRef } from 'react';
import { clsx } from 'clsx';

import { Button, Icon, TIconName, useToggle, Utils, Dropdown, Badge } from '@treezor/design-system';

export interface ICustomPopoverProps {
  label: string;
  content: ReactNode;
  icon?: TIconName;
  isDisabled?: boolean;
  indication?: string | number;
  itemClassName?: string;
  className?: string;
  errorMessage?: string;
  id: string;
}

export const CustomPopover = ({
  label,
  content,
  icon,
  isDisabled,
  indication,
  itemClassName,
  className,
  id,
  errorMessage,
}: ICustomPopoverProps) => {
  const [isOpen, toggleOpen] = useToggle(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div className={clsx('relative', itemClassName)}>
      <div
        className="flex justify-between hover:cursor-pointer group items-center gap-2"
        onClick={() => !isDisabled && toggleOpen()}
        ref={ref}
        data-test-id={`popover-${id}`}
      >
        <div className="flex items-center gap-2">
          <Button
            id={`popover-${Utils.handleTestIdsFormat(id || 'custom')}-${Utils.handleTestIdsFormat(label)}`}
            variant="ghost"
            icon={icon}
            disabled={isDisabled}
            size="s"
            testId={`popover-${Utils.handleTestIdsFormat(id || 'custom')}`}
          >
            {label}
          </Button>
          {indication ? <Badge type="default" label={indication.toString()} /> : null}
        </div>

        <Icon
          name="expand_more"
          size="s"
          className={clsx('group-hover:text-icon-interactive transition-transform', isOpen && 'rotate-180')}
        />
      </div>

      {isOpen && (
        <Dropdown onClose={toggleOpen} parentRef={ref} className={className}>
          {content}
          {errorMessage && (
            <div
              className="typo-body-regular-caption text-error p-4 w-full flex"
              data-testid={`popover-${Utils.handleTestIdsFormat(label)}-error`}
            >
              {errorMessage}
            </div>
          )}
        </Dropdown>
      )}
    </div>
  );
};
